import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DecimalPipe, NgIf } from '@angular/common';
import { Subject, Subscription, interval } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import {
  LeadScope,
  ProspectingService,
  UnlockJob,
  UnlockSummary,
  apiErrorMessage,
  saveBlob,
} from '../../../../services/prospecting.service';
import { PopupService } from '../../../../services/popup.service';
import { ExportColumnsPickerComponent } from '../export-columns-picker/export-columns-picker.component';
import { LeadExportColumn, loadExportColumns, saveExportColumns } from '../../../../utils/lead-export-columns';

const UNLOCK_POLL_MS = 2000;

/**
 * Export the leads of a prospecting list (or an ICP) as CSV, optionally
 * unlocking every locked email first (bulk unlock job, 2 credits per email
 * found, nothing when not found).
 */
@Component({
  selector: 'app-leads-export-modal',
  standalone: true,
  imports: [NgIf, DecimalPipe, ExportColumnsPickerComponent],
  templateUrl: './leads-export-modal.component.html',
})
export class LeadsExportModalComponent implements OnInit, OnDestroy {
  @Input({ required: true }) scope!: LeadScope;
  @Input() includeDismissed = false;
  /** Used to build the downloaded file's name. */
  @Input() fileLabel = 'leads';
  @Output() closed = new EventEmitter<void>();

  summary: UnlockSummary | null = null;
  isLoadingSummary = true;
  summaryError = false;

  phase: 'idle' | 'starting' | 'unlocking' | 'downloading' = 'idle';
  job: UnlockJob | null = null;
  columns: LeadExportColumn[] = loadExportColumns();

  private destroy$ = new Subject<void>();
  private pollSub: Subscription | null = null;

  constructor(
    private prospectingService: ProspectingService,
    private popupService: PopupService,
  ) {}

  ngOnInit(): void {
    this.loadSummary();
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get busy(): boolean {
    return this.phase !== 'idle';
  }

  /** Unlocking is offered only when the Email column is exported. */
  get offerUnlock(): boolean {
    return !!this.summary && this.summary.locked > 0 && this.columns.includes('email');
  }

  get shortOfCredits(): boolean {
    return !!this.summary && this.summary.locked > 0 && this.summary.balance < this.summary.max_cost;
  }

  get jobProgress(): number {
    if (!this.job || !this.job.total) {
      return 0;
    }
    return Math.min(100, Math.round((this.job.processed / this.job.total) * 100));
  }

  loadSummary(): void {
    this.isLoadingSummary = true;
    this.summaryError = false;

    this.prospectingService.getUnlockSummary(this.scope)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (summary) => {
          this.summary = summary;
          this.isLoadingSummary = false;
        },
        error: () => {
          this.isLoadingSummary = false;
          this.summaryError = true;
        }
      });
  }

  close(): void {
    if (this.busy) {
      return;
    }
    this.closed.emit();
  }

  unlockAndExport(): void {
    if (this.busy) {
      return;
    }
    if (this.summary && this.summary.locked === 0) {
      this.exportWithoutUnlocking();
      return;
    }

    this.phase = 'starting';

    this.prospectingService.startUnlockEmails(this.scope)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (started) => {
          this.phase = 'unlocking';
          this.job = {
            job_id: started.job_id, status: 'pending', total: started.total, processed: 0,
            found: 0, not_found: 0, credits_used: 0, stop_reason: null,
          };
          this.pollJob(started.job_id);
        },
        error: (error) => {
          if (error?.status === 422 && error?.error?.error === 'nothing_to_unlock') {
            this.phase = 'idle';
            this.exportWithoutUnlocking();
            return;
          }
          this.phase = 'idle';
          const msg = error?.status === 402
            ? apiErrorMessage(error, 'Not enough credits to unlock these emails.')
            : apiErrorMessage(error, 'Could not start unlocking the emails.');
          this.popupService.notify(msg, '', 'error');
        }
      });
  }

  private pollJob(jobId: string | number): void {
    this.pollSub?.unsubscribe();
    this.pollSub = interval(UNLOCK_POLL_MS)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.prospectingService.getUnlockJob(jobId)),
      )
      .subscribe({
        next: (job) => {
          this.job = job;
          if (job.status === 'completed' || job.status === 'failed') {
            this.pollSub?.unsubscribe();
            this.pollSub = null;

            if (job.status === 'failed' || job.stop_reason === 'error') {
              this.popupService.notify('Unlocking stopped because of an error — exporting what was unlocked.', '', 'warning');
            } else if (job.stop_reason === 'insufficient_credits') {
              this.popupService.notify(
                `Out of credits after ${job.processed} of ${job.total} emails — exporting what was unlocked.`, '', 'warning');
            } else {
              this.popupService.notify(
                `${job.found} email${job.found === 1 ? '' : 's'} unlocked (${job.credits_used} credits).`, '', 'success');
            }
            this.phase = 'idle';
            this.exportWithoutUnlocking();
          }
        },
        error: () => {
          // Transient — the next tick retries.
        }
      });
  }

  exportWithoutUnlocking(): void {
    if (this.busy) {
      return;
    }
    this.phase = 'downloading';

    saveExportColumns(this.columns);
    this.prospectingService.exportCsv(this.scope, this.includeDismissed, this.columns)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          this.phase = 'idle';
          const slug = (this.fileLabel || 'leads').replace(/[^a-z0-9-]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase().slice(0, 60) || 'leads';
          saveBlob(blob, `leads-${slug}-${new Date().toISOString().slice(0, 10)}.csv`);
          this.closed.emit();
        },
        error: () => {
          this.phase = 'idle';
          this.popupService.notify('Could not export the leads. Please try again.', '', 'error');
        }
      });
  }
}
